import { useEffect, useMemo, useState } from "react";
import modalStyle from "../../styles/modal.module.scss";
import CollectionButton from "../CollectionButton";
import LoadingDots from "../LoadingDots";
import { Swiper, SwiperSlide } from "swiper/react";
import { closeIcon, starFillIcon, starOutlineIcon } from "../Icons";
import "swiper/css";
import "swiper/css/pagination";

export default function MobileMaterialModal({
  handleCloseModal,
  onCollectionAction,
  collectionLabel = "Add to Collection",
  collectionActive,
  onPrimaryAction,
  primaryLabel = "Similar Materials",
  materialData,
  vendor,
  title,
  handleImageLoaded,
  imageLoaded,
  description,
  handleChangeImage,
  ROOT_SERVER,
  slug,
  loaded,
  imageNumber,
}) {
  const [imageIndex, setImageIndex] = useState(imageNumber || 0);
  const [isLandscape, setIsLandscape] = useState(false);

  const images = Array.isArray(materialData?.images) ? materialData.images : [];
  const character = Array.isArray(materialData?.character)
    ? materialData.character
    : [];
  const feature = Array.isArray(materialData?.feature)
    ? materialData.feature
    : [];

  const priceValue = useMemo(() => {
    const value = Number(materialData?.price);
    if (Number.isFinite(value)) {
      return value.toFixed(2);
    }
    return "--";
  }, [materialData?.price]);

  useEffect(() => {
    setImageIndex(imageNumber || 0);
  }, [imageNumber]);

  useEffect(() => {
    const updateOrientation = () => {
      if (typeof window === "undefined") {
        return;
      }

      setIsLandscape(window.matchMedia("(orientation: landscape)").matches);
    };

    updateOrientation();
    window.addEventListener("resize", updateOrientation);
    window.addEventListener("orientationchange", updateOrientation);

    return () => {
      window.removeEventListener("resize", updateOrientation);
      window.removeEventListener("orientationchange", updateOrientation);
    };
  }, []);

  const renderLandscape = () => (
    <div className={modalStyle.mobileLandscapeShell}>
      <div className={modalStyle.mobileLandscapeHeader}>
        <div className={modalStyle.mobileCollectionAction}>
          <CollectionButton materialId={materialData.id} />
        </div>
      </div>

      <div className={modalStyle.mobileLandscapeImageWrap}>
        {!imageLoaded ? <div className="spinner" /> : null}

        <Swiper
          className={modalStyle.mobileSwiper}
          pagination={true}
          navigation={true}
          loop={false}
          nested={true}
          touchStartPreventDefault={false}
          onSwiper={(swiper) => swiper.activeIndex === imageIndex}
          onSlideChange={(swiper) => {
            handleChangeImage(swiper.activeIndex);
            setImageIndex(swiper.activeIndex);
          }}
        >
          {images.length >= 1
            ? images.map((item, i) => (
                <SwiperSlide
                  key={item + i}
                  className={modalStyle.mobileLandscapeSlide}
                >
                  <div className={modalStyle.mobileScrollableImageStage}>
                    <img
                      alt={item}
                      className={`${modalStyle.materialImage} ${modalStyle.mobileLandscapeImage} ${modalStyle.mobileScrollableImage}`}
                      src={`${ROOT_SERVER}${vendor}/${slug}/swatch/${item}`}
                      loading="lazy"
                      onLoad={handleImageLoaded}
                    />
                  </div>
                </SwiperSlide>
              ))
            : false}
        </Swiper>
      </div>

      <div className={modalStyle.mobileLandscapeTitleBar}>
        <div className={modalStyle.mobileLandscapeTitleInline}>
          <span className={modalStyle.mobileLandscapeVendorText}>{vendor}</span>
          <span
            className={modalStyle.mobileLandscapeTitleDot}
            aria-hidden="true"
          >
            •
          </span>
          <h3 className={modalStyle.mobileTitle}>{title}</h3>
        </div>
      </div>
    </div>
  );

  return (
    <div className={modalStyle.modalShade} onClick={handleCloseModal}>
      {loaded ? (
        <div
          className={`${modalStyle.materialModal} ${modalStyle.mobileModal}`}
          onClick={(e) => e.stopPropagation()}
        >
          {isLandscape ? (
            renderLandscape()
          ) : (
            <>
              <button
                type="button"
                className={modalStyle.mobileFloatingCloseChip}
                onClick={handleCloseModal}
              >
                <span>Close</span>
                {closeIcon}
              </button>

              <div
                className={`${modalStyle.modalContent} ${modalStyle.mobileContent}`}
              >
                {/* IMAGE CONTAINER START */}
                <div
                  className={`${modalStyle.imageContainer} ${modalStyle.mobileImageContainer}`}
                >
                  <div className={modalStyle.mobileHeaderRow}>
                    <div className={modalStyle.mobileVendorBadge}>{vendor}</div>
                    <div className={modalStyle.mobileCollectionAction}>
                      <CollectionButton materialId={materialData.id} />
                    </div>
                  </div>

                  {!imageLoaded ? <div className="spinner" /> : null}

                  <Swiper
                    className={modalStyle.mobileSwiper}
                    pagination={true}
                    navigation={true}
                    loop={false}
                    nested={true}
                    touchStartPreventDefault={false}
                    onSwiper={(swiper) => swiper.activeIndex === imageIndex}
                    onSlideChange={(swiper) => {
                      handleChangeImage(swiper.activeIndex);
                      setImageIndex(swiper.activeIndex);
                    }}
                  >
                    {images.length >= 1
                      ? images.map((item, i) => (
                          <SwiperSlide
                            key={item + i}
                            className={modalStyle.mobileSwiperSlide}
                          >
                            <div
                              className={modalStyle.mobileScrollableImageStage}
                            >
                              <img
                                alt={item}
                                className={`${modalStyle.materialImage} ${modalStyle.mobileScrollableImage}`}
                                src={`${ROOT_SERVER}${vendor}/${slug}/full/${item}`}
                                loading="lazy"
                                onLoad={handleImageLoaded}
                              />
                            </div>
                          </SwiperSlide>
                        ))
                      : false}
                  </Swiper>

                  <div className={modalStyle.mobileSwatchRow}>
                    {images.map((item, i) => (
                      <div
                        key={item + i}
                        className={`${modalStyle.mobileSwatchDot} ${
                          imageIndex === i
                            ? modalStyle.mobileSwatchDotActive
                            : ""
                        }`}
                      ></div>
                    ))}
                  </div>
                </div>
                {/* IMAGE CONTAINER END */}
              </div>

              <div className={modalStyle.mobileDataWrap}>
                <h3 className={modalStyle.mobileTitle}>{title}</h3>
                <p className={modalStyle.mobileDescription}>{description}</p>

                <div className={modalStyle.modalMetaGrid}>
                  <div className={modalStyle.modalMetaCard}>
                    <span>Substrate</span>
                    <strong>{materialData?.substrate || "--"}</strong>
                  </div>
                  <div className={modalStyle.modalMetaCard}>
                    <span>Type</span>
                    <strong>{materialData?.type || "--"}</strong>
                  </div>
                  <div className={modalStyle.modalMetaCard}>
                    <span>Group</span>
                    <strong>{materialData?.subtype || "--"}</strong>
                  </div>
                </div>

                <div className={modalStyle.modalPriceBlock}>
                  <div className={modalStyle.modalPriceStatusRow}>
                    <div className={modalStyle.modalPriceSide}>
                      <div className={modalStyle.modalPriceRow}>
                        <span className={modalStyle.modalPriceValue}>
                          ${priceValue}
                        </span>
                        <span className={modalStyle.modalPriceUom}>
                          / sq.ft.
                        </span>
                      </div>
                    </div>
                    <div className={modalStyle.modalStatusSide}>
                      <span className={modalStyle.modalStatusLabel}>
                        Status
                      </span>
                      <strong className={modalStyle.modalStatusValue}>
                        {materialData?.status || "--"}
                      </strong>
                    </div>
                  </div>
                  <p className={modalStyle.modalPriceNote}>
                    Price shown for reference only.
                  </p>
                </div>

                <div className={modalStyle.infoBox}>
                  <div className={modalStyle.infoContent}>
                    <div className={modalStyle.infoContentHeader}>
                      <h4>Character</h4>
                    </div>
                    <div className={modalStyle.infoContentBody}>
                      <ul className={modalStyle.infoBulletList}>
                        {character.length > 0 ? (
                          character.map((item, i) => (
                            <li
                              className={modalStyle.infoBulletItem}
                              key={item + i}
                            >
                              {item}
                            </li>
                          ))
                        ) : (
                          <li className={modalStyle.infoBulletItem}>
                            Not available
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>

                  <div className={modalStyle.infoContent}>
                    <div className={modalStyle.infoContentHeader}>
                      <h4>Finish</h4>
                    </div>
                    <div className={modalStyle.infoContentBody}>
                      <ul className={modalStyle.infoBulletList}>
                        {feature.length > 0 ? (
                          feature.map((item, i) => (
                            <li
                              className={modalStyle.infoBulletItem}
                              key={item + i}
                            >
                              {item}
                            </li>
                          ))
                        ) : (
                          <li className={modalStyle.infoBulletItem}>
                            Not available
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className={modalStyle.mobileFooterActions}>
                  <button
                    type="button"
                    className={`${modalStyle.mobileFooterButton} ${modalStyle.mobileFooterButtonSecondary} ${
                      !onCollectionAction ? modalStyle.actionDisabled : ""
                    }`}
                    onClick={onCollectionAction}
                  >
                    {collectionActive ? starFillIcon : starOutlineIcon}
                    <h4>{collectionLabel}</h4>
                  </button>

                  <button
                    type="button"
                    className={`${modalStyle.mobileFooterButton} ${
                      !onPrimaryAction ? modalStyle.actionDisabled : ""
                    }`}
                    onClick={onPrimaryAction}
                  >
                    <svg
                      className={modalStyle.mobileFooterIcon}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M7 4.5L12.5 10L7 15.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <h4>{primaryLabel}</h4>
                  </button>
                  <button
                    type="button"
                    className={`${modalStyle.mobileFooterButton} ${modalStyle.mobileFooterButtonLight}`}
                    onClick={handleCloseModal}
                  >
                    <svg
                      className={modalStyle.mobileFooterIcon}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M6 6l8 8M14 6l-8 8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <h4>Close Window</h4>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={modalStyle.modalStateCard}>
          <LoadingDots
            label="Loading Material Details..."
            labelClassName={modalStyle.modalStateTitle}
            dotsClassName={modalStyle.modalStateDots}
          />
        </div>
      )}
    </div>
  );
}
